import { Hl7MessageType } from '../../../../api';
import { HL7Message } from './hl7-detail.types';
import { IClaimDataPanelViewModel } from './claim-data-detail.types';

const CLAIM_DATA_TYPES = new Set<string>([
    Hl7MessageType.JSONPROVIDERPROFESSIONAL,
    Hl7MessageType.JSONPROVIDERINSTITUTIONAL,
    Hl7MessageType.JSONSUPPORTPROFESSIONAL,
    Hl7MessageType.JSONSUPPORTINSTITUTIONAL,
    'JSON_PROVIDER_PROFESSIONAL',
    'JSON_PROVIDER_INSTITUTIONAL',
    'JSON_SUPPORT_PROFESSIONAL',
    'JSON_SUPPORT_INSTITUTIONAL',
]);

export function isClaimDataHl7Message(type?: string): boolean {
    if (!type) {
        return false;
    }

    return CLAIM_DATA_TYPES.has(type) || type.startsWith('JSON_PROVIDER_') || type.startsWith('JSON_SUPPORT_');
}

const safeParseJson = (text: string): unknown | null => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

export const removeNullObjectFields = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map((item) => removeNullObjectFields(item));
    }

    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        return Object.keys(record).reduce<Record<string, unknown>>((acc, key) => {
            const normalized = removeNullObjectFields(record[key]);
            if (normalized !== null) {
                acc[key] = normalized;
            }
            return acc;
        }, {});
    }

    return value;
};

export function extractClaimDataPayload(hl7: HL7Message): unknown {
    const record = hl7 as Record<string, unknown>;

    if (record.supportProfessional) {
        return record.supportProfessional;
    }
    if (record.providerProfessional) {
        return record.providerProfessional;
    }
    if (record.supportInstitutional) {
        return record.supportInstitutional;
    }
    if (record.providerInstitutional) {
        return record.providerInstitutional;
    }
    if (record.professional) {
        return record.professional;
    }
    if (record.institutional) {
        return record.institutional;
    }

    if (hl7.message) {
        const parsed = safeParseJson(hl7.message);
        if (parsed !== null) {
            return parsed;
        }
    }

    return {};
}

function getClaimKindLabel(type?: string): string {
    const normalized = type || '';
    if (normalized.includes('INSTITUTIONAL')) {
        return 'Institutional claim';
    }

    if (normalized.includes('PROFESSIONAL')) {
        return 'Professional claim';
    }

    return 'Claim payload';
}

export function getDefaultClaimDataLabel(num?: number): string {
    if (num === 1) {
        return 'Initial claim payload';
    }

    if (num != null && num > 1) {
        return 'Claim data update';
    }

    return 'Claim payload';
}

function countArrayField(payload: Record<string, unknown>, key: string): number {
    const value = payload[key];
    return Array.isArray(value) ? value.length : 0;
}

export function buildClaimDataPanelViewModel(
    claimData: HL7Message,
    options: { num?: number; label?: string; sourceLabel?: string } = {},
): IClaimDataPanelViewModel {
    const payload = removeNullObjectFields(extractClaimDataPayload(claimData));
    const payloadRecord =
        payload && typeof payload === 'object' && !Array.isArray(payload)
            ? (payload as Record<string, unknown>)
            : {};

    return {
        label: options.label ?? getDefaultClaimDataLabel(options.num),
        id: claimData.id,
        type: claimData.type,
        created: claimData.created,
        controlNumber:
            typeof payloadRecord.controlNumber === 'string' ? payloadRecord.controlNumber : undefined,
        claimKindLabel: getClaimKindLabel(claimData.type),
        serviceCount: countArrayField(payloadRecord, 'services') || countArrayField(payloadRecord, 'serviceLines'),
        providerCount: countArrayField(payloadRecord, 'providers'),
        sourceLabel: options.sourceLabel ?? 'External System',
        payload,
    };
}
